import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { store } from './store';
import { useAppSelector, useAppDispatch } from './store';
import { setUser } from './store/authSlice';

// Import components
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import Vehicles from './pages/Vehicles';
import Maintenance from './pages/Maintenance';
import Parking from './pages/Parking';
import Equipment from './pages/Equipment';
import SubClients from './pages/SubClients';
import SubClientSheet from './pages/SubClientSheet';
import ConditionAssessments from './pages/ConditionAssessments';
import EmailTemplate from './pages/EmailTemplate';
import VehicleDetail from './pages/VehicleDetail';
import ParkingEditor from './pages/ParkingEditor';
import FieldAgent from './pages/FieldAgent';
import PricingPlans from './pages/Pricing/PricingPlans';
import PricingPacks from './pages/Pricing/PricingPacks';
import PricingGrid from './pages/Pricing/PricingGrid';
import Prestations from './pages/Prestations/Prestations';
import PackPrestations from './pages/Prestations/PackPrestations';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Technicians from './pages/Technicians';
import StopSell from './pages/StopSell';
import ReservationsList from './pages/Reservations/ReservationsList';
import ReservationCreate from './pages/Reservations/ReservationCreate';
import Agencies from './pages/Reservations/Agencies';
import Planning from './pages/Reservations/Planning';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1976d2',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Main App component
const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);

  // Load user data from localStorage on app startup
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData && !user) {
      try {
        const parsedUser = JSON.parse(userData);
        dispatch(setUser(parsedUser));
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, [dispatch, user]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Router>
          <ErrorBoundary>
            <Routes>
              {/* Public routes */}
              <Route 
                path="/login" 
                element={
                  isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
                } 
              />
              
              {/* Field Agent routes */}
              <Route
                path="/field-agent/*"
                element={
                  <ProtectedRoute>
                    <FieldAgent />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    {/* Redirect field agents to mobile interface */}
                    {(user?.role?.name === 'field_agent' || user?.role?.name === 'agent_terrain' || user?.role?.name === 'agent_technique') ? (
                      <Navigate to="/field-agent" replace />
                    ) : (
                      <Layout>
                        <ErrorBoundary>
                          <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route 
                              path="/dashboard" 
                              element={
                                user?.role?.name === 'admin' ? <AdminDashboard /> : <ClientDashboard />
                              } 
                            />
                            <Route path="/vehicles" element={<Vehicles />} />
                            <Route path="/stop-sell" element={<StopSell />} />
                            <Route path="/vehicles/:id" element={<VehicleDetail />} />
                            <Route path="/maintenance" element={<Maintenance />} />
                            <Route path="/parking" element={<Parking />} />
                            <Route path="/parking/editor" element={<ParkingEditor />} />
                            <Route path="/equipment" element={<Equipment />} />
                            <Route path="/sub-clients" element={<SubClients />} />
                            <Route path="/sub-clients/:subClientId" element={<SubClientSheet />} />
                            <Route path="/condition-assessments" element={<ConditionAssessments />} />
                            <Route path="/email-template" element={<EmailTemplate />} />
                            {/* Tarification - Simplified navigation */}
                            <Route path="/pricing" element={<Navigate to="/pricing/plans" replace />} />
                            <Route path="/pricing/plans" element={<PricingPlans />} />
                            <Route path="/pricing/packs" element={<PricingPacks />} />
                            <Route path="/pricing/grid" element={<PricingGrid />} />
                            {/* Redirect old tiers route to grid */}
                            <Route path="/pricing/tiers" element={<Navigate to="/pricing/grid" replace />} />
                            {/* Prestations */}
                            <Route path="/prestations" element={<Prestations />} />
                            <Route path="/prestations/packs" element={<PackPrestations />} />
                            {/* Reservations */}
                            <Route path="/reservations" element={<ReservationsList />} />
                            <Route path="/reservations/planning" element={<Planning />} />
                            <Route path="/reservations/agencies" element={<Agencies />} />
                            <Route path="/reservations/create" element={<ReservationCreate />} />
                            <Route path="/reservations/:id" element={<ReservationCreate />} />
                            <Route path="/reservations/:id/edit" element={<ReservationCreate />} />
                            {/* Redirect old pricing route to grid */}
                            <Route path="/prestations/pricing" element={<Navigate to="/pricing/grid" replace />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/settings/technicians" element={<Technicians />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                          </Routes>
                        </ErrorBoundary>
                      </Layout>
                    )}
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ErrorBoundary>
        </Router>
      </Box>
    </ThemeProvider>
  );
};

// Root App component with Redux provider
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
